import { mergeStyles } from '@cian/utils';
import memoize from 'lodash/memoize';
import * as React from 'react';

import { createBodyScroll } from './body_scroll';
import { Comment, IImageComment } from './comment';
import { translateY } from './css';
import { isSafari } from './device_detector';
import { GalleryHeader } from './header';
import styles from './index.css';
import { IGalleryTab } from './model';
import { Tabs } from './tabs';
import { ZoomButtons } from './zoom_buttons';
import { IFotoramaData } from '../../../../typings/fotorama';
import { GalleryOverview } from '../popup_gallery/overview';
import { Slider } from '../slider';
import { SlideSource } from '../slider/slide/slide';
import { ICoords } from '../slider/types';
import { PlayerState, VIDEO_IS_PLAYING_STATES } from '../slider/youtube';

const VERTICAL_SWIPE_DISTANCE = 150;
const FULL_OPACITY_DISTANCE = 50;
const bodyScroll = createBodyScroll();

export interface IImageSlide {
  fullUrl: string;
  thumbUrl: string;
}

interface IGalleryProps {
  slides: IFotoramaData[];
  tabs?: IGalleryTab[];
  comments: (IImageComment | null)[];
  startWithSlide?: number;
  slideDidChange?(slideIndex: number): void;
  onClose(): void;
  onPlayerStateChange?(state: PlayerState): void;
}

interface IGalleryState {
  currentSlideIndex: number;
  showInterface: boolean;
  offsetY: number;
  offsetX: number;
  currentComment: IImageComment | null;
  movingAxis: 'x' | 'y' | 'both';
  duringTransition: boolean;
  zoom: number;
}

function slideFromUrl(slide: IFotoramaData): SlideSource {
  return { status: 'loaded' as const, slide };
}

export class PopupGallery extends React.PureComponent<IGalleryProps, IGalleryState> {
  public state: IGalleryState = {
    currentSlideIndex: this.props.startWithSlide || 0,
    showInterface: true,
    offsetY: 0,
    offsetX: 0,
    movingAxis: 'both',
    duringTransition: false,
    zoom: 1,
    currentComment: this.props.comments[this.props.startWithSlide || 0],
  };

  private slider: Slider;
  private lastTouchTime = 0;

  private getSlides = memoize((urls: IFotoramaData[]): SlideSource[] => {
    return urls.map(slideFromUrl);
  });

  public componentDidMount() {
    if (!isSafari(navigator.userAgent)) {
      bodyScroll.disable();
    }
  }

  public componentWillUnmount() {
    if (!isSafari(navigator.userAgent)) {
      bodyScroll.enable();
    }
  }

  public componentDidUpdate(prevProps: IGalleryProps, prevState: IGalleryState) {
    if (this.props.slideDidChange && prevState.currentSlideIndex !== this.state.currentSlideIndex) {
      this.props.slideDidChange(this.state.currentSlideIndex);
    }
  }

  public render() {
    const { state, props } = this;
    const { slides } = props;
    const allSlidesCount = slides.length;
    const sliderSlides = this.getSlides(slides);
    const offsetY = state.movingAxis === 'y' || state.movingAxis === 'both' ? state.offsetY : 0;
    const transform = translateY(offsetY, 'px');
    const motionStyles = { transform, WebkitTransform: transform };
    const showZoomButtons = !slides[state.currentSlideIndex].video;

    const containerStyle = {
      opacity:
        FULL_OPACITY_DISTANCE < Math.abs(offsetY)
          ? Math.max(0, 1 - (Math.abs(offsetY) - FULL_OPACITY_DISTANCE) / VERTICAL_SWIPE_DISTANCE)
          : 1,
    };
    const onTabChange = (tab: IGalleryTab) => {
      this.setSlide(tab.startIndex);
    };

    const { currentComment } = state;

    return (
      <div
        {...mergeStyles(containerStyle, styles['gallery'], state.duringTransition && styles['transition'])}
        onTouchStart={this.handleTouchStart}
      >
        <GalleryHeader
          currentSlide={state.currentSlideIndex}
          slidesCount={allSlidesCount}
          visible={state.showInterface}
          onClose={this.props.onClose}
        />
        <Tabs
          tabs={props.tabs || []}
          imageIndex={state.currentSlideIndex}
          onChange={onTabChange}
          visible={state.showInterface}
        />
        <div
          {...mergeStyles(styles['slider_wrapper'], motionStyles, state.duringTransition && styles['transition'])}
          onTransitionEnd={this.onTransitionEnd}
        >
          <Slider
            height="100%"
            slides={sliderSlides}
            slideWillChange={this.setCurrentSlideIndex}
            zoomWillChange={this.updateZoomInState}
            onSlideMove={this.onSlideMove}
            preventXMoving={state.movingAxis === 'y'}
            onSlideMovingEnd={this.onSlideMovingEnd}
            onSlideTransitionEnd={this.resetYOffset}
            onClick={this.toggleInterface}
            ref={this.setSlider}
            noInterface={!state.showInterface}
            onPlayerStateChange={this.handlePlayerStateChange}
          />
        </div>
        {showZoomButtons && <ZoomButtons max={3} min={1} step={0.5} value={state.zoom} onChange={this.setZoom} />}
        {currentComment && <Comment comment={currentComment.comment} />}
        <GalleryOverview
          slides={slides}
          currentSlide={state.currentSlideIndex}
          setSlide={this.setSlide}
          visible={state.showInterface}
          offsetX={state.offsetX / state.zoom}
        />
      </div>
    );
  }
  private handlePlayerStateChange = (state: PlayerState) => {
    if (this.props.onPlayerStateChange) {
      this.props.onPlayerStateChange(state);
    }

    let showInterface = true;

    if (VIDEO_IS_PLAYING_STATES.includes(state)) {
      showInterface = false;
    }

    this.setState({ showInterface });
  };
  private updateZoomInState = (zoom: number) => {
    this.setState({ zoom });
  };
  private setZoom = (zoom: number) => {
    this.updateZoomInState(zoom);
    this.slider.updateCurrentSlideZoom(() => zoom);
  };
  private onSlideMove = (touchOffsetCoords: ICoords, realCoords: ICoords) => {
    this.setState({ offsetX: -touchOffsetCoords.x });
    const diff = touchOffsetCoords.y - realCoords.y;

    if (realCoords.y !== diff) {
      this.setState({ offsetY: diff });
    }

    this.setState(state => {
      // Если изображение вылазит за пределы экрана по оси y
      // разрешаем двигать как угодно
      const outsideOfScreenOnYAxis = realCoords.y !== 0;
      if (outsideOfScreenOnYAxis) {
        const state = { movingAxis: 'both' };

        return state;
      }

      if (state.movingAxis !== 'both') {
        return {};
      }

      if (Math.abs(state.offsetX) > 10) {
        return { movingAxis: 'x' };
      }

      if (Math.abs(state.offsetY) > 10) {
        return { movingAxis: 'y' };
      }

      return { movingAxis: 'both' };
    });
  };
  private resetYOffset = () => {
    if (!this.shouldClose(this.state.offsetY)) {
      this.setState({ movingAxis: 'both', offsetY: 0 });
    }
  };
  private onTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) {
      return;
    }

    this.setState({ duringTransition: false });
    if (this.shouldClose(this.state.offsetY)) {
      this.props.onClose();
    } else {
      this.resetYOffset();
    }
  };
  private onSlideMovingEnd = () => {
    this.setState(state => {
      if (state.movingAxis === 'x') {
        return {};
      }

      const isSwipeUp = -state.offsetY > VERTICAL_SWIPE_DISTANCE / 2;
      const isSwipeDown = state.offsetY > VERTICAL_SWIPE_DISTANCE / 2;

      const fullOffset = VERTICAL_SWIPE_DISTANCE + FULL_OPACITY_DISTANCE;

      const offsetY = isSwipeDown ? fullOffset : isSwipeUp ? -fullOffset : 0;
      const newState = { duringTransition: true, offsetY };

      return newState;
    });
  };
  private shouldClose = (offsetY: number) => {
    return Math.abs(offsetY) >= VERTICAL_SWIPE_DISTANCE;
  };
  private toggleInterface = () => {
    this.setState(state => ({
      showInterface: !state.showInterface,
    }));
  };
  private setSlide = (index: number) => {
    this.slider.setSlide(index);
    this.setCurrentSlideIndex(index);
  };
  private setSlider = (slider: Slider) => {
    if (!slider) {
      return;
    }
    this.slider = slider;
    this.slider.setSlide(this.state.currentSlideIndex);
  };
  private setCurrentSlideIndex = (newIndex: number) => {
    this.setState({ currentSlideIndex: newIndex, currentComment: this.props.comments[newIndex] });
  };
  private handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    e.stopPropagation();

    const newTime = new Date().getTime();
    const minDelta = 300;

    if (Math.abs(this.lastTouchTime - newTime) < minDelta) {
      e.preventDefault();
    }

    this.lastTouchTime = newTime;
  };
}
